import { Form, Modal } from "react-bootstrap";
import { useEffect, useState } from "react";
import { CircularProgress, Grid, TextField } from "@mui/material";
import { Box } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import axios from "axios";
import { toast } from "react-toastify";
import { dadosUF } from "../../../../components/dadosFixos/UF";
import { dadosPais } from "../../../../components/dadosFixos/Pais";
import FormActions from "../../../../components/Formularios/Buttons/FormActions";
import CustomTextField from "../../../../components/Formularios/Inputs/CustomTextField";
import CustomSelect from "../../../../components/Formularios/Inputs/CustomSelect";
import SwitchAtivo from "../../../../components/Formularios/Inputs/SwitchAtivo";

interface FilialInterface {
  id_filial?: number;
  fantasia: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  cmun?: string;
  cep?: string;
  uf?: string;
  cuf?: string;
  pais?: string;
  cpais?: string;
  fone?: string;
  celular?: string;
  email?: string;
  responsavel?: string;
  situacao: number;
  id_empresa?: number;
}

interface DetalheModalProps {
  showModal: boolean;
  acao?: string;
  onClose: () => void;
  onSubmit: (formData: FilialInterface) => void;
  dadosFilial?: FilialInterface;
}

const opcoesUF = dadosUF.map((item) => ({ valor: item.uf, label: item.nome }));
const opcoesPais = dadosPais.map((item) => ({ valor: item.codpais, label: item.pais }));

const titulosPorAcao: Record<string, string> = {
  Novo: "Incluir Filial",
  Visualizar: "Visualizar Filial",
};

const ModalDetalheFilial: React.FC<DetalheModalProps> = ({
  showModal,
  acao,
  onClose,
  onSubmit,
  dadosFilial
}) => {
  const { register, handleSubmit, setValue, reset, control } = useForm<FilialInterface>();
  const [loading, setLoading] = useState<boolean>(false);

  const isVisualizando = acao === "Visualizar";

  useEffect(() => {
    if (showModal && dadosFilial) {
      setValue("fantasia", dadosFilial.fantasia || "");
      setValue("logradouro", dadosFilial.logradouro || "");
      setValue("numero", dadosFilial.numero || "");
      setValue("complemento", dadosFilial.complemento || "");
      setValue("bairro", dadosFilial.bairro || "");
      setValue("cidade", dadosFilial.cidade || "");
      setValue("cmun", dadosFilial.cmun || "");
      setValue("cep", dadosFilial.cep || "");
      setValue("uf", dadosFilial.uf || "");
      setValue("cpais", dadosFilial.cpais || "1058"); // Brasil como padrão
      setValue("fone", dadosFilial.fone || "");
      setValue("celular", dadosFilial.celular || "");
      setValue("email", dadosFilial.email || "");
      setValue("responsavel", dadosFilial.responsavel || "");
      setValue("situacao", dadosFilial.situacao === 1 ? 1 : 0);
    } else if (showModal) {
      reset();
      setValue("situacao", 1);
      setValue("cpais", "1058"); // Brasil como padrão
    }
  }, [showModal, dadosFilial]);

  const buscarCep = async (cep: string) => {
    const cepLimpo = cep.replace(/\D/g, '');

    if (cepLimpo.length < 8) {
      toast.error("CEP inválido! O CEP deve conter 8 dígitos.");
      return;
    }

    setLoading(true);

    try {
      const { data } = await axios.get(`https://viacep.com.br/ws/${cepLimpo}/json/`);

      if (data.erro) {
        toast.error("CEP inválido ou não localizado!");
        return;
      }

      setValue("logradouro", data.logradouro || "");
      setValue("complemento", data.complemento || "");
      setValue("bairro", data.bairro || "");
      setValue("cidade", data.localidade || "");
      setValue("uf", data.uf || "");
      setValue("cmun", data.ibge || "");

      const cepFormatado = cepLimpo.replace(/^(\d{5})(\d{3})$/, "$1-$2");
      setValue("cep", cepFormatado);

      toast.success("CEP localizado com sucesso!");
    } catch (erro: any) {
      console.error("Erro ao buscar CEP:", erro);
      const msg = erro.response?.data.message === undefined ? erro.message : erro.response.data.message;
      toast.error(`Erro ao buscar CEP. Motivo: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const formatarCep = (value: string) => {
    const cepLimpo = value.replace(/\D/g, '');
    const cepLimitado = cepLimpo.slice(0, 8);

    if (cepLimitado.length > 5) {
      return `${cepLimitado.slice(0, 5)}-${cepLimitado.slice(5)}`;
    }

    return cepLimitado;
  };

  const handleFormSubmit = (data: FilialInterface) => {
    const formData = {
      ...data,
      situacao: data.situacao === 1 ? 1 : 0,
      id_empresa: dadosFilial?.id_empresa
    };
    onSubmit(formData);
  };

  return (
    <Modal
      show={showModal}
      className="modal-full-width"
      size="xl"
      keyboard={false}
      centered
    >
      <Modal.Header className="bg-white">
        <Modal.Title>{titulosPorAcao[acao ?? ""] ?? "Editar Filial"}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit(handleFormSubmit)}>
        <Modal.Body className="bg-white">
          <Box sx={{ width: "100%", pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <CustomTextField
                  label="Nome Fantasia"
                  name="fantasia"
                  register={register}
                  rules={{ required: true }}
                  required
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <CustomTextField
                  label="Responsável"
                  name="responsavel"
                  register={register}
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <SwitchAtivo name="situacao" control={control} disabled={isVisualizando} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <CustomTextField
                  label="Telefone"
                  name="fone"
                  register={register}
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  label="Celular"
                  name="celular"
                  register={register}
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  label="Email"
                  name="email"
                  register={register}
                  rules={{ required: true }}
                  required
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} sm={6} />

              <Grid item xs={12} sm={6} md={2}>
                <Controller
                  name="cep"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="CEP"
                      size="small"
                      fullWidth
                      value={field.value || ""}
                      onChange={(e) => {
                        const formattedValue = formatarCep(e.target.value);
                        field.onChange(formattedValue);
                      }}
                      onBlur={(e) => {
                        field.onBlur();
                        buscarCep(e.target.value);
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      disabled={loading || isVisualizando}
                      InputProps={{
                        endAdornment: loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomTextField
                  label="Logradouro"
                  name="logradouro"
                  register={register}
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <CustomTextField
                  label="Complemento"
                  name="complemento"
                  register={register}
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <CustomTextField
                  label="Número"
                  name="numero"
                  register={register}
                  disabled={isVisualizando}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <CustomTextField
                  label="Bairro"
                  name="bairro"
                  register={register}
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  label="Cidade"
                  name="cidade"
                  register={register}
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <CustomSelect
                  label="Estado"
                  name="uf"
                  control={control}
                  opcoes={opcoesUF}
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <CustomTextField
                  label="Cod. IBGE"
                  name="cmun"
                  register={register}
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <CustomSelect
                  label="País"
                  name="cpais"
                  control={control}
                  opcoes={opcoesPais}
                  disabled={isVisualizando}
                />
              </Grid>
            </Grid>
          </Box>
        </Modal.Body>
        <Modal.Footer className="bg-white">
          <FormActions onVoltar={onClose} visualizar={isVisualizando} variante="modal" />
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ModalDetalheFilial;
